import React, { useEffect, useId, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Check, ChevronDown, ExternalLink, FolderOpen, Loader2, Play, Plus, RefreshCw, X } from 'lucide-react';
import {
  findTiaCpu,
  hasTiaProjectRegistry,
  isTiaEntryOpen,
  sameTiaProjectRef,
  TIA_DEFAULT_CPU_NOTE,
  TIA_READINESS_AWAITING_SOMEONE,
  TIA_VALIDATION_CODES,
  tiaFamilyCpuNote,
  tiaUnrecognisedCpuNote,
  type PlcModelSource,
  type ResolvedTiaTarget,
  type TiaAgentFacts,
  type TiaCpu,
  type TiaProjectEntry,
  type TiaProjectRef,
  type TiaProjectSource,
  type TiaText,
  type TiaWorkMode,
} from '@realplc/types';
import { useTranslation } from '../../i18n';
import {
  OPEN_TIA_PROJECT_PICKER_EVENT,
  openTiaProject,
  readTiaPickerOutcome,
  readTiaReadiness,
  refreshTiaProjects,
  startTiaPortal,
  TiaRequestError,
  type OpennessAgentRecord,
  type OpenTiaProjectPickerDetail,
  type TiaPickerActionResult,
} from '../../services/opennessAgent';
import { useSessionTiaTargetStore } from '../../stores/sessionTiaTargetStore';
import { useTiaAgentStatusStore } from '../../stores/tiaAgentStatusStore';
import { CPU_SOURCE_WORDS, TIA_WORK_MODE_TEXT, tiaRefusalWords } from '../../utils/tiaSituations';
import { TiaAgentUpgradeEntry } from './TiaAgentUpgradeEntry';

/**
 * The picker's words, once, in both languages. The three groups are the
 * sources the user named on 2026-09-23: projects open in TIA, projects the
 * Agent used or created, and project files found in its work folder.
 */
export const TIA_PROJECT_PICKER_TEXT: {
  trigger: TiaText;
  title: TiaText;
  intro: TiaText;
  groups: Readonly<Record<TiaProjectSource, TiaText>>;
  sources: Readonly<Record<TiaProjectSource, TiaText>>;
  readOnly: TiaText;
  useOpenProject: TiaText;
  newProject: TiaText;
  newProjectNeedsUpgrade: TiaText;
  notChosen: TiaText;
  close: TiaText;
  storeFailed: TiaText;
  refresh: TiaText;
  refreshSent: TiaText;
  openSelected: TiaText;
  opened: TiaText;
  openSent: TiaText;
  startTia: TiaText;
  started: TiaText;
  startSent: TiaText;
  actionFailed: TiaText;
} = {
  trigger: { zh: 'TIA 工程', en: 'TIA project' },
  title: { zh: '选择 TIA 工程', en: 'Choose the TIA project' },
  intro: {
    zh: '这个对话的程序在所选工程里编译验证。',
    en: "This conversation's program is compiled and validated in the project chosen here.",
  },
  groups: {
    open: { zh: 'TIA 里打开的', en: 'Open in TIA' },
    recent: { zh: 'Agent 用过或新建的', en: 'Used or created by the Agent' },
    scanned: { zh: '工作目录里扫到的', en: "Found in the Agent's work folder" },
  },
  sources: {
    open: { zh: '已打开', en: 'open' },
    recent: { zh: 'Agent 用过或新建', en: 'used or created by the Agent' },
    scanned: { zh: '工作目录', en: 'work folder' },
  },
  readOnly: {
    zh: '旧版 Agent 只能用 TIA 里开着的这个工程',
    en: 'An older Agent can use only the project open in TIA',
  },
  useOpenProject: { zh: '使用 TIA 里开着的工程', en: 'Use the project open in TIA' },
  newProject: { zh: '新建工程', en: 'New project' },
  newProjectNeedsUpgrade: { zh: '升级 Agent 后可用', en: 'Available after upgrading the Agent' },
  notChosen: { zh: '未选择', en: 'none chosen' },
  close: { zh: '关闭', en: 'Close' },
  storeFailed: {
    zh: '所选工程没能马上保存，下一轮对话会带上它。',
    en: 'The chosen project could not be stored yet; the next turn carries it.',
  },
  refresh: { zh: '刷新', en: 'Refresh' },
  refreshSent: { zh: '已请 Agent 刷新工程列表', en: 'Asked the Agent to list its projects again' },
  openSelected: { zh: '在 TIA 里打开', en: 'Open in TIA' },
  opened: { zh: '已在 Agent 的 TIA 窗口里打开', en: 'Opened in the Agent\'s TIA window' },
  openSent: { zh: '已发给 Agent，TIA 窗口稍后打开', en: 'Sent to the Agent; its TIA window opens shortly' },
  startTia: { zh: '启动 TIA', en: 'Start TIA' },
  // True whether the Agent started it or found it running (`alreadyRunning`).
  started: { zh: 'TIA 已在 Agent 那台电脑上运行', en: 'TIA is running on the Agent machine' },
  startSent: { zh: '已发给 Agent，TIA 稍后启动', en: 'Sent to the Agent; TIA starts shortly' },
  actionFailed: { zh: 'Agent 没能完成', en: 'The Agent could not do it' },
};

type PickerAction = 'refresh' | 'open' | 'start';

/** Where the picker's last action on the Agent stands. */
type PickerActionState =
  | { kind: 'idle' }
  | { kind: 'pending'; action: PickerAction }
  | { kind: 'said'; words: TiaText; failed: boolean };

export interface TiaProjectPickerProps {
  /** The conversation's RealPLC project. Without one nothing is validated, and the picker shows nothing. */
  projectId?: string | null;
  /** How the Agent creates a project, from the user's settings (E12): 「新建工程」 says where. */
  workMode: TiaWorkMode;
  /**
   * The composer's PLC model box, the setting every turn sends: before a turn
   * resolves the conversation's CPU, 「新建工程」 names the one it will name.
   */
  settingsPlcModel?: string;
  /**
   * The user chose `ref` for the conversation `sessionId`, null for a new
   * conversation before its first turn, whose first turn carries the choice.
   * Called once the choice is in the session target store, dirty, for the
   * next turn to send.
   *
   * The resume seam, which ChatInput wires (T11): T8's endpoint stores the
   * reference in the session snapshot and resumes a run parked on
   * TIA_PROJECT_NOT_SELECTED or TIA_PROJECT_REF_NOT_FOUND, then
   * sessionTiaTargetStored takes in what was stored, and the choice is no
   * longer unsent. A promise that rejects leaves the choice dirty for the next
   * turn, and the picker says so.
   *
   * `ref` is null when the user cleared the reference (an old Agent's
   * 「使用 TIA 里开着的工程」), which the endpoint stores as a clear.
   */
  onProjectChosen?: (sessionId: string | null, ref: TiaProjectRef | null) => void | Promise<void>;
}

/** One project the picker lists, on one Agent. */
interface ProjectRow {
  key: string;
  group: TiaProjectSource;
  displayName: string;
  tiaVersion?: string;
  cpu?: string;
  /** The Agent's name, when more than one Agent lists projects. */
  agentName?: string;
  /** What choosing it sets; none for an old Agent's open project, which is shown read-only (E1). */
  ref?: TiaProjectRef;
}

const GROUP_ORDER: readonly TiaProjectSource[] = ['open', 'recent', 'scanned'];

const entryKey = (agentId: string, id: string) => `entry:${agentId}/${id}`;
const legacyKey = (agentId: string) => `legacy:${agentId}`;

/**
 * Where a project is listed: with the open ones while it is open in the
 * user's TIA, its `_RPV_` validation copy included (isTiaEntryOpen, E7);
 * otherwise by its source. The Agent reports a project it listed open as
 * recent once it is closed (T6); a closed entry that still says open is read
 * the same way, so no open group ever holds a closed project.
 */
function groupOf(entry: TiaProjectEntry): TiaProjectSource {
  if (isTiaEntryOpen(entry)) return 'open';
  return entry.source === 'scanned' ? 'scanned' : 'recent';
}

/**
 * A project's CPU by name: the table's model for an article it holds; for one
 * of a family whose model the table lacks, the family and the article, since
 * the family's representative would name another CPU; anything else as the
 * Agent reported it.
 */
function cpuLabel(orderNumber: string | undefined): string | undefined {
  const article = orderNumber?.trim();
  if (!article) return undefined;
  const found = findTiaCpu(article);
  if (found?.match === 'exact') return found.cpu.model;
  if (found?.match === 'family') return `${found.cpu.family} · ${article}`;
  return article;
}

/**
 * The workspace's online Agents. The gateway authorizes access to the
 * conversation's RealPLC project separately from the workstation connection.
 */
function listingAgents(agents: TiaAgentFacts[] | undefined): TiaAgentFacts[] {
  return (agents ?? []).filter((agent) => agent.online);
}

function projectRows(listing: TiaAgentFacts[], records: OpennessAgentRecord[] | undefined): ProjectRow[] {
  const named = listing.length > 1;
  return listing.flatMap((agent): ProjectRow[] => {
    const { agentId } = agent;
    const agentName = named ? records?.find((record) => record.id === agentId)?.agentName || agentId : undefined;
    if (!hasTiaProjectRegistry(agent)) {
      // E1: an Agent before P2 keeps no registry and compiles only in the project open today.
      return agent.openProject
        ? [{ key: legacyKey(agentId), group: 'open', displayName: agent.openProject.displayName, tiaVersion: agent.tiaVersion, agentName }]
        : [];
    }
    return agent.projects.map((entry) => ({
      key: entryKey(agentId, entry.id),
      group: groupOf(entry),
      displayName: entry.displayName,
      tiaVersion: entry.tiaVersion,
      cpu: cpuLabel(entry.cpuOrderNumber),
      agentName,
      ref: { kind: 'agent', agentId, id: entry.id, displayName: entry.displayName, source: entry.source },
    }));
  });
}

/**
 * A CPU and where it came from, in the default note's shape: 「CPU：S7-1500
 * CPU 1511-1 PN（来自需求）」. A model the table does not hold goes as written,
 * which already says CPU.
 */
function cpuFrom(cpu: TiaCpu | undefined, text: string, source: 'requirement' | 'settings'): TiaText {
  const [en, zh] = CPU_SOURCE_WORDS[source];
  return cpu
    ? { zh: `CPU：${cpu.family} ${cpu.model}（${zh}）`, en: `CPU: ${cpu.family} ${cpu.model} (${en})` }
    : { zh: `${text}（${zh}）`, en: `${text} (${en})` };
}

/**
 * The CPU a new project gets, as far as the page can tell before the turn
 * resolves it, in the turn's own order (E9 ①, ai-runtime `resolveTargetCpu`):
 * - the model a turn of this conversation resolved from the requirement;
 * - else the settings box, read as the turn reads it: a model by name, a
 *   family with its note, and anything else with the default and the words
 *   quoted (E9 ③);
 * - else the default, with its note.
 * A model resolved from the settings is the box as it was then, so the box
 * as it is now stands in for it. Never the selected project's CPU: that is an
 * existing project's, not the new one's.
 */
function newProjectCpu(
  resolved: { plcModel?: string; plcModelSource?: PlcModelSource },
  settingsPlcModel: string | undefined,
): TiaText {
  if (resolved.plcModel && resolved.plcModelSource === 'requirement') {
    const named = findTiaCpu(resolved.plcModel);
    return cpuFrom(named?.match === 'exact' ? named.cpu : undefined, resolved.plcModel, 'requirement');
  }
  const setting = settingsPlcModel?.trim();
  if (!setting) return TIA_DEFAULT_CPU_NOTE;
  const named = findTiaCpu(setting);
  if (named?.match === 'exact') return cpuFrom(named.cpu, setting, 'settings');
  if (named?.match === 'family') return tiaFamilyCpuNote(named, 'settings');
  return tiaUnrecognisedCpuNote(setting);
}

/** The row the target names, if one does. */
function currentRowKey(target: ResolvedTiaTarget): string | undefined {
  if (target.kind === 'entry') return entryKey(target.agentId, target.entry.id);
  if (target.kind === 'legacy_open') return legacyKey(target.agentId);
  return undefined;
}

/**
 * What the picker names as the conversation's project: the target, else,
 * while nothing can be compiled in (no Agent, or a block), the user's own
 * choice, which the reference keeps the name of for exactly this.
 */
function targetName(target: ResolvedTiaTarget, ref: TiaProjectRef | undefined): string | TiaText {
  if (target.kind === 'entry') return target.entry.displayName;
  if (target.kind === 'legacy_open') return target.displayName;
  if (target.kind === 'new' || ref?.kind === 'new') return TIA_PROJECT_PICKER_TEXT.newProject;
  if (ref?.kind === 'agent') return ref.displayName;
  return TIA_PROJECT_PICKER_TEXT.notChosen;
}

/**
 * Tab and Shift+Tab wrap around a modal list instead of leaving it for the
 * page behind the backdrop; from the list itself, which the pop-up focuses,
 * Shift+Tab goes to its last control.
 */
function keepTabInside(event: React.KeyboardEvent, panel: HTMLElement | null): void {
  if (!panel) return;
  const controls = Array.from(panel.querySelectorAll<HTMLElement>(
    'button:not(:disabled), [href], input:not(:disabled), [tabindex]:not([tabindex="-1"])',
  ));
  const first = controls[0];
  const last = controls[controls.length - 1];
  if (!first || !last) {
    event.preventDefault();
    return;
  }
  const active = document.activeElement;
  const onPanel = active === panel;
  const inside = !onPanel && active instanceof Node && panel.contains(active);
  if (event.shiftKey) {
    if (active === first || !inside) {
      event.preventDefault();
      last.focus();
    }
  } else if (active === last || (!inside && !onPanel)) {
    // From the list itself, Tab reaches its first control on its own.
    event.preventDefault();
    first.focus();
  }
}

interface Opened {
  as: 'popover' | 'modal';
  /** 「新建工程」 comes preselected, when the gate's 「新建工程」 opened the picker. */
  preselectNew?: boolean;
}

/**
 * The TIA project picker above the composer's input box (TIA 根治 P2 T5).
 *
 * It names the project the conversation's next turn validates in, and lists
 * what the conversation's Agents report, by source, with 「新建工程」. Which
 * project is the target, and E7's default when nothing is chosen (the one
 * open project, a new one, or a choice among several), is resolveTiaTarget's
 * alone, read through readTiaReadiness; the picker writes no rule of its own.
 *
 * Choosing sets the conversation's reference in useSessionTiaTargetStore,
 * dirty, and the next turn sends it; 「新建工程」 sets `{ kind: 'new' }` and
 * creates nothing (E11 ④). An Agent from before P2 is listed read-only, and
 * cannot create a project (E1).
 *
 * It opens by itself, as a modal, when several projects are open and none is
 * chosen (E7 ②): once per conversation until a target settles, which the
 * session target store remembers (`projectChoicePrompted`) across the remount
 * a conversation's first turn causes; and when the gate asks for it
 * (OPEN_TIA_PROJECT_PICKER_EVENT).
 */
export const TiaProjectPicker: React.FC<TiaProjectPickerProps> = ({ projectId, workMode, settingsPlcModel, onProjectChosen }) => {
  const { language } = useTranslation();
  const say = (text: TiaText) => (language === 'Chinese' ? text.zh : text.en);
  const status = useTiaAgentStatusStore((state) => state.status);
  const sessionRef = useSessionTiaTargetStore((state) => state.tiaProjectRef);
  const plcModel = useSessionTiaTargetStore((state) => state.plcModel);
  const plcModelSource = useSessionTiaTargetStore((state) => state.plcModelSource);
  const prompted = useSessionTiaTargetStore((state) => state.projectChoicePrompted);
  const dirty = useSessionTiaTargetStore((state) => state.tiaProjectRefDirty);
  const [opened, setOpened] = useState<Opened | null>(null);
  // Storing the choice right away failed; it stays dirty, and a turn that stores it clears this.
  const [storeFailed, setStoreFailed] = useState(false);
  const [agentAction, setAgentAction] = useState<PickerActionState>({ kind: 'idle' });
  const containerRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  /** Whether closing gives focus back; not when a press elsewhere closed it, which takes focus there. */
  const restoreFocusRef = useRef(true);
  const titleId = useId();

  const project = projectId || undefined;
  const visible = Boolean(project);
  const view = useMemo(() => readTiaReadiness(status, project, sessionRef), [status, project, sessionRef]);
  // What 「新建工程」 resolves to: an Agent before P2 cannot create a project (E1).
  const newTarget = useMemo(() => readTiaReadiness(status, project, { kind: 'new' }).target, [status, project]);
  const listing = useMemo(() => (project ? listingAgents(status?.tiaAgents) : []), [status, project]);
  const rows = useMemo(() => projectRows(listing, status?.agents), [listing, status]);

  const needsChoice = visible && view.state === 'no_project_selected';
  // A target resolved on an Agent that has probed, ready or busy: the choice
  // is settled, and several projects needing one again is a new occasion. A
  // blip offline or back into checking settles nothing.
  const choiceSettled = visible && (view.state === 'ready' || view.state === 'busy');
  useEffect(() => {
    const target = useSessionTiaTargetStore.getState();
    if (needsChoice && !prompted) {
      setOpened({ as: 'modal' });
      target.setProjectChoicePrompted(true);
    } else if (choiceSettled && prompted) {
      target.setProjectChoicePrompted(false);
    }
  }, [needsChoice, choiceSettled, prompted]);

  useEffect(() => {
    if (!visible) return undefined;
    const onOpen = (event: Event) => {
      const intent = (event as CustomEvent<OpenTiaProjectPickerDetail>).detail?.intent;
      setOpened({ as: 'modal', preselectNew: intent === 'new' });
    };
    window.addEventListener(OPEN_TIA_PROJECT_PICKER_EVENT, onOpen);
    return () => window.removeEventListener(OPEN_TIA_PROJECT_PICKER_EVENT, onOpen);
  }, [visible]);

  // Focus goes to the preselected row, else to the list itself: a list that
  // pops up while someone types must not take their Enter as a choice. When
  // the list closes, focus goes back where it was, unless a press elsewhere
  // closed it.
  useEffect(() => {
    if (!opened) return undefined;
    restoreFocusRef.current = true;
    const returnTo = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const panel = panelRef.current;
    const preselected = panel?.querySelector<HTMLElement>('[data-preselected="true"]:not(:disabled)');
    (preselected ?? panel)?.focus();
    return () => {
      if (restoreFocusRef.current && returnTo?.isConnected) returnTo.focus();
    };
  }, [opened]);

  useEffect(() => {
    if (!dirty) setStoreFailed(false);
  }, [dirty]);

  useEffect(() => {
    if (opened?.as !== 'popover') return undefined;
    const onPointerDown = (event: MouseEvent) => {
      if (containerRef.current?.contains(event.target as Node)) return;
      restoreFocusRef.current = false;
      setOpened(null);
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [opened]);

  if (!visible) return null;

  // Keys pressed in the picker, its modal included, whose events React passes
  // up through the portal: Escape closes the list, and Tab stays in the modal.
  // An Escape anywhere else, the @ autocomplete's in the textarea among them,
  // is not the picker's.
  const onKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (!opened) return;
    if (event.key === 'Escape') {
      event.stopPropagation();
      setOpened(null);
    } else if (event.key === 'Tab' && opened.as === 'modal') {
      keepTabInside(event, panelRef.current);
    }
  };

  // Hands the choice to the resume seam; a failure keeps it dirty, and says so.
  const report = (ref: TiaProjectRef | null) => {
    setStoreFailed(false);
    const pending = onProjectChosen?.(useSessionTiaTargetStore.getState().sessionId, ref);
    if (pending) pending.catch(() => setStoreFailed(true));
  };

  const choose = (ref: TiaProjectRef) => {
    setOpened(null);
    const target = useSessionTiaTargetStore.getState();
    // The conversation's choice already, sent or not: nothing to set, or to store.
    if (sameTiaProjectRef(target.tiaProjectRef, ref)) return;
    target.setSessionTiaProjectRef(ref);
    report(ref);
  };

  // E1 with a reference in the way (T5 review): an Agent from before P2 can
  // use no reference, so a chosen project, or 「新建工程」, leaves it blocked
  // for good. Its open project's row then clears the reference, and E7's
  // default resolves to that project again.
  const refBlocksOldAgent = sessionRef !== undefined
    && (view.state === 'upgrade_required' || view.state === 'project_not_found');
  const clearChoice = () => {
    setOpened(null);
    useSessionTiaTargetStore.getState().clearSessionTiaProjectRef();
    report(null);
  };

  // The Agent the header's actions go to (T11): the target's, else the only
  // one listing projects here. An Agent from before P2 declares none of them.
  const actionAgent = listing.find((agent) => agent.agentId === view.agentId) ?? (listing.length === 1 ? listing[0] : undefined);
  const selectedEntry = view.target.kind === 'entry' && view.target.agentId === actionAgent?.agentId ? view.target.entry : undefined;
  const runAgentAction = async (
    action: PickerAction,
    request: () => Promise<TiaPickerActionResult>,
    words: { done: TiaText; sent: TiaText },
  ) => {
    setAgentAction({ kind: 'pending', action });
    // A code's words when it has them: a refusal, or how a done action came
    // about (a project already open).
    const said = (code: string | undefined, fallback: TiaText, failed: boolean): PickerActionState => {
      const [en, zh] = tiaRefusalWords(code, [fallback.en, fallback.zh]);
      return { kind: 'said', failed, words: { zh, en } };
    };
    try {
      const ended = readTiaPickerOutcome(await request());
      setAgentAction(ended.outcome === 'failed'
        ? said(ended.code, TIA_PROJECT_PICKER_TEXT.actionFailed, true)
        : said(ended.code, ended.outcome === 'done' ? words.done : words.sent, false));
    } catch (error) {
      setAgentAction(said(error instanceof TiaRequestError ? error.code : undefined, TIA_PROJECT_PICKER_TEXT.actionFailed, true));
    }
  };
  const actionPending = agentAction.kind === 'pending';
  const actionIcon = (action: PickerAction, Icon: typeof RefreshCw) => (
    agentAction.kind === 'pending' && agentAction.action === action
      ? <Loader2 size={12} className="animate-spin" />
      : <Icon size={12} />
  );
  const toolButton = 'inline-flex items-center gap-1 rounded-md border border-gray-200 px-2 py-1 text-[11px] font-medium text-zinc-700 transition-colors hover:bg-zinc-50 disabled:cursor-not-allowed disabled:opacity-50 dark:border-zinc-700 dark:text-zinc-200 dark:hover:bg-white/[0.04]';
  const toolbar = !project || !actionAgent ? null : hasTiaProjectRegistry(actionAgent) ? (
    <div className="flex flex-wrap items-center gap-1.5 border-b border-gray-100 px-3 py-2 dark:border-zinc-800">
      <button
        type="button"
        disabled={actionPending}
        onClick={() => void runAgentAction('refresh', () => refreshTiaProjects(project, actionAgent.agentId), {
          done: TIA_PROJECT_PICKER_TEXT.refreshSent,
          sent: TIA_PROJECT_PICKER_TEXT.refreshSent,
        })}
        className={toolButton}
      >
        {actionIcon('refresh', RefreshCw)}
        {say(TIA_PROJECT_PICKER_TEXT.refresh)}
      </button>
      <button
        type="button"
        disabled={actionPending || !selectedEntry}
        onClick={() => {
          if (selectedEntry) {
            void runAgentAction('open', () => openTiaProject(project, actionAgent.agentId, selectedEntry.id), {
              done: TIA_PROJECT_PICKER_TEXT.opened,
              sent: TIA_PROJECT_PICKER_TEXT.openSent,
            });
          }
        }}
        className={toolButton}
      >
        {actionIcon('open', ExternalLink)}
        {say(TIA_PROJECT_PICKER_TEXT.openSelected)}
      </button>
      <button
        type="button"
        disabled={actionPending}
        onClick={() => void runAgentAction('start', () => startTiaPortal(project, actionAgent.agentId), {
          done: TIA_PROJECT_PICKER_TEXT.started,
          sent: TIA_PROJECT_PICKER_TEXT.startSent,
        })}
        className={toolButton}
      >
        {actionIcon('start', Play)}
        {say(TIA_PROJECT_PICKER_TEXT.startTia)}
      </button>
      {agentAction.kind === 'said' ? (
        <span role="status" className={`text-[11px] ${agentAction.failed ? 'text-rose-700 dark:text-rose-300' : 'text-zinc-500 dark:text-zinc-400'}`}>
          {say(agentAction.words)}
        </span>
      ) : null}
    </div>
  ) : (
    // E1: an Agent from before P2 takes none of these; upgrading is the way on.
    <div className="border-b border-gray-100 px-3 py-2 text-zinc-700 dark:border-zinc-800 dark:text-zinc-200">
      <TiaAgentUpgradeEntry />
    </div>
  );

  const currentKey = currentRowKey(view.target);
  const newIsCurrent = view.target.kind === 'new';
  const newNeedsUpgrade = newTarget.kind === 'blocked' && newTarget.code === TIA_VALIDATION_CODES.AGENT_UPGRADE_REQUIRED;
  const newCpu = newProjectCpu({ plcModel, plcModelSource }, settingsPlcModel);
  const waitsOnYou = TIA_READINESS_AWAITING_SOMEONE.has(view.state);
  // Why the list waits, or why it is empty, in the readiness table's words;
  // nothing before the first status has told anything.
  const hint = status && (waitsOnYou || listing.length === 0) ? say(view.explanation) : undefined;
  const name = targetName(view.target, sessionRef);
  const nameText = typeof name === 'string' ? name : say(name);
  const { trigger } = TIA_PROJECT_PICKER_TEXT;

  const rowClass = (current: boolean, interactive: boolean) => `flex w-full items-start gap-2 rounded-lg px-3 py-2 text-left text-sm transition-colors ${
    current ? 'bg-cyan-50 text-cyan-900 dark:bg-cyan-950/40 dark:text-cyan-100' : 'text-zinc-700 dark:text-zinc-200'
  } ${interactive ? 'hover:bg-zinc-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-cyan-500/60 dark:hover:bg-white/[0.04]' : ''}`;

  const renderRow = (row: ProjectRow) => {
    const current = row.key === currentKey;
    const meta = [
      row.tiaVersion ? `TIA ${row.tiaVersion}` : undefined,
      say(TIA_PROJECT_PICKER_TEXT.sources[row.group]),
      row.cpu,
      row.agentName,
    ].filter(Boolean).join(' · ');
    const body = (
      <>
        <FolderOpen size={14} className="mt-0.5 shrink-0 opacity-60" />
        <span className="min-w-0 flex-1">
          <span className="block truncate font-medium">{row.displayName}</span>
          <span className="block truncate text-[11px] text-zinc-500 dark:text-zinc-400">{meta}</span>
          {row.ref ? null : (
            <span className="block text-[11px] text-zinc-500 dark:text-zinc-400">
              {say(refBlocksOldAgent ? TIA_PROJECT_PICKER_TEXT.useOpenProject : TIA_PROJECT_PICKER_TEXT.readOnly)}
            </span>
          )}
        </span>
        {current ? <Check size={14} className="mt-0.5 shrink-0 text-cyan-600 dark:text-cyan-400" aria-hidden="true" /> : null}
      </>
    );
    const { ref } = row;
    if (!ref && refBlocksOldAgent) {
      return (
        <button type="button" onClick={clearChoice} className={rowClass(current, true)}>
          {body}
        </button>
      );
    }
    if (!ref) {
      return <div className={rowClass(current, false)} aria-current={current ? 'true' : undefined}>{body}</div>;
    }
    return (
      <button type="button" onClick={() => choose(ref)} aria-current={current ? 'true' : undefined} className={rowClass(current, true)}>
        {body}
      </button>
    );
  };

  const panel = (
    <div
      ref={panelRef}
      role="dialog"
      aria-modal={opened?.as === 'modal' ? true : undefined}
      aria-labelledby={titleId}
      tabIndex={-1}
      className="overflow-hidden rounded-xl border border-gray-200 bg-white shadow-lg outline-none dark:border-cyan-900/50 dark:bg-[#161b22]"
    >
      <div className="flex items-start justify-between gap-3 border-b border-gray-100 px-3 py-2 dark:border-zinc-800">
        <div className="min-w-0">
          <p id={titleId} className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{say(TIA_PROJECT_PICKER_TEXT.title)}</p>
          <p className="text-[11px] text-zinc-500 dark:text-zinc-400">{say(TIA_PROJECT_PICKER_TEXT.intro)}</p>
        </div>
        <button
          type="button"
          onClick={() => setOpened(null)}
          aria-label={say(TIA_PROJECT_PICKER_TEXT.close)}
          className="shrink-0 rounded-md p-1 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-600 dark:hover:bg-zinc-800 dark:hover:text-zinc-300"
        >
          <X size={14} />
        </button>
      </div>
      {toolbar}
      {hint ? (
        <p className="border-b border-amber-100 bg-amber-50/70 px-3 py-2 text-xs leading-relaxed text-amber-800 dark:border-amber-900/40 dark:bg-amber-950/20 dark:text-amber-200">
          {hint}
        </p>
      ) : null}
      <div className="custom-scrollbar max-h-72 overflow-y-auto p-1">
        {GROUP_ORDER.map((group) => {
          const inGroup = rows.filter((row) => row.group === group);
          if (inGroup.length === 0) return null;
          const headingId = `${titleId}-${group}`;
          return (
            <div key={group} role="group" aria-labelledby={headingId}>
              <p id={headingId} className="px-3 pb-1 pt-2 text-[10px] font-semibold uppercase tracking-wider text-zinc-400 dark:text-zinc-500">
                {say(TIA_PROJECT_PICKER_TEXT.groups[group])}
              </p>
              <ul>
                {inGroup.map((row) => <li key={row.key}>{renderRow(row)}</li>)}
              </ul>
            </div>
          );
        })}
      </div>
      <div className="border-t border-gray-100 p-1 dark:border-zinc-800">
        <button
          type="button"
          disabled={newNeedsUpgrade}
          onClick={() => choose({ kind: 'new' })}
          aria-current={newIsCurrent ? 'true' : undefined}
          data-preselected={opened?.preselectNew ? 'true' : undefined}
          className={`${rowClass(newIsCurrent, !newNeedsUpgrade)} disabled:cursor-not-allowed disabled:opacity-50`}
        >
          <Plus size={14} className="mt-0.5 shrink-0 opacity-60" />
          <span className="min-w-0 flex-1">
            <span className="block font-medium">{say(TIA_PROJECT_PICKER_TEXT.newProject)}</span>
            {newNeedsUpgrade ? (
              <span className="block text-[11px] text-zinc-500 dark:text-zinc-400">{say(TIA_PROJECT_PICKER_TEXT.newProjectNeedsUpgrade)}</span>
            ) : (
              <>
                <span className="block text-[11px] leading-relaxed text-zinc-500 dark:text-zinc-400">{say(newCpu)}</span>
                <span className="block text-[11px] text-zinc-500 dark:text-zinc-400">{say(TIA_WORK_MODE_TEXT.newProject[workMode])}</span>
              </>
            )}
          </span>
          {newIsCurrent ? <Check size={14} className="mt-0.5 shrink-0 text-cyan-600 dark:text-cyan-400" aria-hidden="true" /> : null}
        </button>
      </div>
    </div>
  );

  // The container is not positioned: the list is laid out against the input
  // box's wrapper, exactly as the @ and / autocomplete is (ComposerPicker), so
  // it is as wide as the box however far in the box starts.
  return (
    <div ref={containerRef} className="mb-1.5" onKeyDown={onKeyDown}>
      <button
        type="button"
        onClick={() => setOpened((current) => (current ? null : { as: 'popover' }))}
        aria-haspopup="dialog"
        aria-expanded={opened !== null}
        aria-label={say({ zh: `${trigger.zh}：${nameText}`, en: `${trigger.en}: ${nameText}` })}
        title={waitsOnYou ? say(view.explanation) : undefined}
        className={`inline-flex max-w-full items-center gap-1.5 rounded-md border px-2 py-1 text-[11px] transition-colors ${waitsOnYou
          ? 'border-amber-200 bg-amber-50/70 text-amber-800 hover:bg-amber-100 dark:border-amber-800 dark:bg-amber-950/30 dark:text-amber-300'
          : 'border-gray-200 bg-gray-50 text-gray-600 hover:border-cyan-300 hover:bg-cyan-50 dark:border-cyan-900/35 dark:bg-zinc-900/45 dark:text-zinc-300 dark:hover:bg-cyan-900/25'}`}
      >
        <FolderOpen size={12} className="shrink-0" />
        <span className="shrink-0 font-semibold">{say(trigger)}</span>
        <span className="min-w-0 truncate">{nameText}</span>
        <ChevronDown size={12} className="shrink-0 opacity-60" />
      </button>
      {storeFailed ? (
        <p role="status" className="mt-1 text-[11px] text-amber-700 dark:text-amber-300">{say(TIA_PROJECT_PICKER_TEXT.storeFailed)}</p>
      ) : null}
      {opened?.as === 'popover' ? (
        <div className="absolute bottom-full left-0 z-50 mb-2 w-full max-w-md">{panel}</div>
      ) : null}
      {opened?.as === 'modal' ? createPortal(
        <div
          className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4 backdrop-blur-sm"
          onMouseDown={(event) => {
            if (event.target === event.currentTarget) setOpened(null);
          }}
        >
          <div className="w-full max-w-md">{panel}</div>
        </div>,
        document.body,
      ) : null}
    </div>
  );
};
